'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import attachEditor from 'LayoutBuilderUI/components/editors/BaseEditor';
import WidgetPlaceholder from 'LayoutBuilderUI/components/placeholders/widgets/WidgetPlaceholder';
import { getIsRichTextModule, getIsVideoModule, getSelectedSmartRuleBody } from 'EmailEditorUI/utils/moduleUtils';
import { getShouldUsePlaceholderForModule } from 'EmailEditorUI/utils/emailPlaceholderUtils';
import { getLanguageMessagesToUse } from 'ContentEditorUI/lib/InpageUtils';
import { renderModule as renderModuleInParent } from 'LayoutBuilderUI/actions/ApiActions';
import { getModuleSpec, getIsClientRenderedModule } from 'EmailEditorUI/redux/selectors/ModuleSelectors';
import { getSchemaForModule } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import { getIsTransactional } from 'EmailEditorUI/redux/selectors/subscriptionSelectors';
import AsyncRichTextWithConfig from 'LayoutBuilderUI/components/fields/overrides/AsyncRichTextWithConfig';
import ElementContext from 'LayoutBuilderUI/components/editors/ElementContext';
import { NamespaceWrapper } from 'EmailEditorUI/components/NamespaceWrapper';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import classNames from 'classnames';
import GrayScaleWhenHidden from 'LayoutBuilderUI/components/overlays/GrayScaleWhenHidden';
import { CenteredLoadingSpinner } from 'LayoutBuilderUI/components/CenteredLoadingSpinner';
import getLang from 'I18n/utils/getLang';
import { DEFERRED_LOADING_TIMEOUT, DEFERRED_LOADING_TIMEOUT_RENDERING } from 'LayoutBuilderUI/lib/LoadingConstants';
import { getIsCustomEmail } from 'EmailEditorUI/redux/selectors/emailCategoriesSelectors';
import { getLanguage } from 'EmailEditorUI/redux/selectors/EmailSelectors';
import CommentableArea from 'LayoutBuilderUI/components/utility/CommentableArea';
import { getSelectedId } from 'EmailEditorUI/redux/selectors/selected';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CACHE_BUSTER_REGEX = /&t=[0-9]+/g;
const DEFAULT_INTERVAL_FOR_VIDEO_POLLING = 3000;
const mapStateToProps = (state, props) => {
  const {
    element
  } = props;
  const isCustomEmail = getIsCustomEmail(state);
  let moduleSpec = getModuleSpec(state, element);
  if (getIsCustomEmail(state) && (!moduleSpec || !moduleSpec.fields)) {
    moduleSpec = getSchemaForModule(state, element.toJS());
  }
  const isClientRendered = getIsClientRenderedModule(props.element, state);
  return {
    isSelected: getSelectedId(state) === props.element.get('id'),
    isClientRendered,
    moduleSpec,
    isTransactional: getIsTransactional(state),
    isCustomEmail,
    renderedLanguage: getLanguage(state) || getLang(),
    shouldRenderPlaceholder: getShouldUsePlaceholderForModule({
      module: element,
      moduleSpec,
      checkRenderedOutput: !isClientRendered,
      renderedOutput: props.renderedOutput,
      state
    })
  };
};
export class CustomWidgetBase extends Component {
  constructor(props) {
    super(props);
    this.outputContainsImgs = () => {
      return this.props.renderedOutput && this.props.renderedOutput.getHtml() && this.props.renderedOutput.getHtml().includes('<img');
    };
    this.updateOverlayHtml = () => {
      if (this.outputContainsImgs() && this.state.overlayHtml !== this.props.renderedOutput.getHtml()) {
        this.setState({
          overlayHtml: this.props.renderedOutput.getHtml()
        });
      }
    };
    this.onLoadImage = e => {
      if (this.timeouts.videoRefresh && this.firstImageStartTime) {
        const imageLoadTime = new Date().getTime() - this.firstImageStartTime;
        this.firstImageStartTime = null;
        if (imageLoadTime > DEFAULT_INTERVAL_FOR_VIDEO_POLLING) {
          clearInterval(this.timeouts.videoRefresh);
          this.timeouts.videoRefresh = null;
          this.handleVideoThumbnailPolling(imageLoadTime * 2);
        }
      }
      this._loadingImgRefs = this._loadingImgRefs.filter(i => i !== e.target);
      if (this._loadingImgRefs.length === 0) {
        this.deferLoading(false, 'imagesLoading');
        this.updateOverlayHtml();
      }
    };
    this.onErrorImage = e => {
      this.onLoadImage(e);
      if (this.timeouts.videoRefresh) {
        clearInterval(this.timeouts.videoRefresh);
      }
    };
    this.getRef = ref => {
      this.elementRef = ref;
    };
    this.maybeRenderLoading = () => {
      if (!this.state.imagesLoading && !this.state.moduleRendering) return null;
      return /*#__PURE__*/_jsx(NamespaceWrapper, {
        children: /*#__PURE__*/_jsx(CenteredLoadingSpinner, {
          "data-test-id": "element-loader",
          "data-test-loading": classNames(this.state.imagesLoading && `images-loading-${this._loadingImgRefs.length}`, this.state.moduleRendering && 'module-rendering')
        })
      });
    };
    this.maybeRenderWidgetContent = () => {
      if (this.props.isInModuleOverlay) {
        return null;
      }
      return this.maybeRenderErroredState() || this.maybeRenderPlaceholder() || this.renderModule();
    };
    this.handleLoadImages = this.handleLoadImages.bind(this);
    this.deferLoading = this.deferLoading.bind(this);
    this._loadingImgRefs = [];
    this.state = {
      moduleRendering: this.outputIsRendering(props),
      imagesLoading: false,
      overlayHtml: props.renderedOutput ? props.renderedOutput.getHtml() : ''
    };
    this.timeouts = {};
  }
  componentDidMount() {
    this.handleLoadImages();
  }
  shouldComponentUpdate(nextProps) {
    if (nextProps.element.get('selectedSmartRuleId')) {
      // This case can happen when value changes are synced down
      // before/after selectedSmartRuleId
      return !!getSelectedSmartRuleBody(nextProps.element);
    }
    return true;
  }
  componentDidUpdate(prevProps) {
    if (this.props.isClientRendered) return;
    if (this.props.renderedOutput !== prevProps.renderedOutput) {
      if (this.outputIsRendering(this.props) && !this.outputIsRendering(prevProps)) {
        const timeoutDelay = DEFERRED_LOADING_TIMEOUT_RENDERING - (Date.now() - this.props.renderedOutput.renderStartTime);
        this.deferLoading(true, 'moduleRendering', timeoutDelay);
        this.deferLoading(false, 'imagesLoading');
      } else if (this.outputIsRendering(prevProps) && !this.outputIsRendering(this.props)) {
        this.handleLoadImages();
        this.deferLoading(false, 'moduleRendering');
        if (this.props.isSelected && this.elementRef) {
          this.elementRef.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
        return;
      }
      this.handleLoadImages();
      this.handleVideoThumbnailPolling();
    }
  }
  componentWillUnmount() {
    if (this.timeouts.videoRefresh) {
      clearInterval(this.timeouts.videoRefresh);
    }
  }
  outputIsRendering(props) {
    return props.renderedOutput && props.renderedOutput.isRendering();
  }

  // the thumbnail is generated async so we want to regularly reload the thumbnail
  handleVideoThumbnailPolling(interval = DEFAULT_INTERVAL_FOR_VIDEO_POLLING) {
    const {
      shouldRenderPlaceholder
    } = this.props;
    if (!this.timeouts.videoRefresh && getIsVideoModule(this.props.element) && !shouldRenderPlaceholder) {
      this.timeouts.videoRefresh = setInterval(() => {
        if (this.elementRef) {
          const imgElements = this.elementRef.getElementsByTagName('img');
          if (imgElements.length) {
            const cacheBusterValue = `&t=${new Date().getTime()}`;
            let videoSrc = imgElements[0].src;
            if (videoSrc.includes('composeType=play_button')) {
              if (videoSrc.match(CACHE_BUSTER_REGEX)) {
                videoSrc = videoSrc.replace(CACHE_BUSTER_REGEX, cacheBusterValue);
              } else {
                videoSrc += cacheBusterValue;
              }
            }
            imgElements[0].src = videoSrc;
          }
        }
      }, interval);
    }
  }
  deferLoading(shouldShowLoading, loadingKey = 'moduleRendering', timeout = DEFERRED_LOADING_TIMEOUT) {
    clearTimeout(this.timeouts[loadingKey]);
    this.timeouts[loadingKey] = null;
    if (shouldShowLoading) {
      // do not bother with timeout if we're already showing a loader
      if (this.state.moduleRendering || this.state.imagesLoading) {
        this.setState({
          [loadingKey]: true
        });
      } else {
        this.timeouts[loadingKey] = setTimeout(() => {
          this.setState({
            [loadingKey]: true
          });
        }, Math.min(timeout, DEFERRED_LOADING_TIMEOUT));
      }
    } else if (this.state[loadingKey]) {
      this.setState({
        [loadingKey]: false
      });
    }
  }
  handleLoadImages() {
    if (this.elementRef) {
      const imgs = [...this.elementRef.getElementsByTagName('img')].filter(img => !img.complete && !this._loadingImgRefs.includes(img));
      if (imgs.length) {
        this._loadingImgRefs = imgs;
        this.firstImageStartTime = new Date().getTime();
        for (let i = 0; i < imgs.length; i++) {
          imgs[i].onload = this.onLoadImage;
          imgs[i].onerror = this.onErrorImage;
        }
        this.deferLoading(true, 'imagesLoading');
      } else {
        this.updateOverlayHtml();
      }
    }
  }
  getValue() {
    return getSelectedSmartRuleBody(this.props.element);
  }
  maybeRenderErroredState() {
    if (this.props.renderedOutput && this.props.renderedOutput.isErrored()) {
      return /*#__PURE__*/_jsx(NamespaceWrapper, {
        children: /*#__PURE__*/_jsxs(ErrorMessage, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "layoutBuilder.elements.errorLoading.title"
          }),
          illustrationProps: {
            width: '100px'
          },
          margin: "0",
          children: [/*#__PURE__*/_jsx("p", {
            children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: "layoutBuilder.elements.errorLoading.message"
            })
          }), /*#__PURE__*/_jsx(Button, {
            use: "tertiary",
            size: "xs",
            onClick: () => this.props.retryRenderModule(this.props.element, false, null, null, false, renderModuleInParent),
            children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: "layoutBuilder.elements.errorLoading.button"
            })
          })]
        })
      });
    }
    return null;
  }
  maybeRenderPlaceholder() {
    const {
      element,
      fileHover,
      moduleSpec,
      shouldRenderPlaceholder
    } = this.props;
    if (!shouldRenderPlaceholder) return null;
    return /*#__PURE__*/_jsx(WidgetPlaceholder, {
      elementId: element.get('id'),
      element: element,
      moduleSpec: moduleSpec,
      fileHover: fileHover
    });
  }
  renderModule() {
    const {
      renderedOutput
    } = this.props;
    if (this.props.isInModuleOverlay) {
      // @bend-expect trellis-tokenized
      return /*#__PURE__*/_jsx("div", {
        ref: this.getRef,
        className: "rendered"
      });
    }
    if (!this.outputContainsImgs()) {
      return (
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx("div", {
          ref: this.getRef,
          className: "rendered",
          dangerouslySetInnerHTML: {
            __html: renderedOutput.getHtml()
          }
        })
      );
    }
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsxs("div", {
        style: {
          position: 'relative'
        },
        className: "modulePositioner",
        children: [/*#__PURE__*/_jsx("div", {
          ref: this.getRef,
          className: "rendered",
          dangerouslySetInnerHTML: {
            __html: renderedOutput.getHtml()
          }
        }), /*#__PURE__*/_jsx("div", {
          className: "renderedOverlay",
          dangerouslySetInnerHTML: {
            __html: this.state.overlayHtml
          }
        })]
      })
    );
  }
  render() {
    const {
      renderedOutput,
      element,
      isClientRendered,
      moduleSpec,
      isSelected,
      isCustomEmail,
      isInFlexArea
    } = this.props;
    const loadingSpinner = this.maybeRenderLoading();
    if (isClientRendered) {
      if (getIsRichTextModule(element) && !this.props.isBlocked) {
        let elementHtml = this.getValue().get('html');
        if (elementHtml !== '' && !elementHtml) {
          let messages = getLanguageMessagesToUse(moduleSpec.messages, this.props.renderedLanguage);
          if (!messages) {
            messages = moduleSpec.messages['en'];
          }
          elementHtml = messages && messages['html.default'] && messages['html.default'].message || '';
        }
        const fieldKey = moduleSpec.fields && moduleSpec.fields.length > 0 && moduleSpec.fields[0].name ? moduleSpec.fields[0].name : 'html';
        const renderedElement = /*#__PURE__*/_jsx(GrayScaleWhenHidden, {
          showIcon: true,
          children: /*#__PURE__*/_jsxs(ElementContext.Provider, {
            value: {
              id: element.get('id'),
              elementHtml
            },
            children: [!isSelected && this.maybeRenderPlaceholder(), /*#__PURE__*/_jsx(AsyncRichTextWithConfig, {
              fieldKey: fieldKey,
              id: element.get('id'),
              isSelected: isSelected,
              selectedSmartRuleId: element.get('selectedSmartRuleId'),
              value: elementHtml,
              readOnly: false,
              isCustomEmail: isCustomEmail,
              isInline: true,
              setTinyMceRef: this.props.setTinyMceRef,
              isInFlexArea: isInFlexArea
            })]
          })
        });
        if (isCustomEmail && !isInFlexArea) {
          return /*#__PURE__*/_jsx(CommentableArea, {
            moduleId: `custom-widget-${element.get('id')}`,
            description: {
              translationKey: 'emailCustomWidget',
              options: {
                label: element.get('label')
              }
            },
            children: renderedElement
          });
        }
        return renderedElement;
      }
    }
    const overlayDoesntMatch = this.outputContainsImgs() && this.state.overlayHtml !== renderedOutput.getHtml();
    const renderingModule = this.outputIsRendering(this.props) || !!loadingSpinner || overlayDoesntMatch;
    const wrapperClasses = classNames('moduleWrapper', renderingModule && "renderingModule", !!loadingSpinner && "hasLoadingSpinner");
    const dataTestAttributes = classNames(this.state, overlayDoesntMatch && "overlayDoesntMatch");
    const renderedElement =
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs("div", {
      className: wrapperClasses,
      "data-test": dataTestAttributes,
      children: [loadingSpinner, /*#__PURE__*/_jsx(GrayScaleWhenHidden, {
        showIcon: true,
        children: this.maybeRenderWidgetContent()
      })]
    });
    if (isCustomEmail && !isInFlexArea) {
      return /*#__PURE__*/_jsx(CommentableArea, {
        "data-test-id": "commentable-area-wrapper",
        moduleId: `custom-widget-${element.get('id')}`,
        description: {
          translationKey: 'emailCustomWidget',
          options: {
            label: element.get('label')
          }
        },
        children: renderedElement
      });
    }
    return renderedElement;
  }
}
CustomWidgetBase.propTypes = {
  isClientRendered: PropTypes.bool.isRequired,
  moduleSpec: PropTypes.object.isRequired,
  element: PropTypes.object.isRequired,
  renderedOutput: PropTypes.object,
  fileHover: PropTypes.bool,
  setTinyMceRef: PropTypes.func,
  retryRenderModule: PropTypes.func,
  isSelected: PropTypes.bool,
  isCustomEmail: PropTypes.bool.isRequired,
  isBlocked: PropTypes.bool,
  renderedLanguage: PropTypes.string.isRequired,
  shouldRenderPlaceholder: PropTypes.bool.isRequired,
  isInModuleOverlay: PropTypes.bool,
  isInFlexArea: PropTypes.bool
};
CustomWidgetBase.defaultProps = {
  isBlocked: false,
  isInModuleOverlay: false
};
export const CustomWidget = connect(mapStateToProps)(attachEditor(CustomWidgetBase));